import {
  createCheckoutTracker,
  type CheckoutTracker,
  type CheckoutTrackerOrderContext,
  type CheckoutTrackerScenario,
} from '@gig/checkout-shared-tracker';
import { __ENV__, __REGION__ } from '../api/constant.js';
import type { IOrderInfo, IPayResult } from '../types/order.js';
import { createCheckoutUuid } from '../utils/uuid.js';

declare const __APP_VERSION__: string;

export const FRONT_TRACKER_APP_CODE = 'acquirer-checkout-front';

/**
 *
 * 创建 front 收银台页使用的 redirect tracker。
 *
 * 这里固定把 `appCode` 和 `sdkCode` 都归属到 `acquirer-checkout-front`，
 * 因为当前页面只是 redirect 容器壳层，还没有进入 `checkout-web` 的实际支付执行链路。
 *
 * @param sessionUuid 当前收银台链路使用的会话标识，默认由浏览器生成
 * @returns front 壳层使用的 tracker 实例
 */
export function createFrontTracker(sessionUuid: string = createCheckoutUuid()): CheckoutTracker {
  return createCheckoutTracker({
    session: {
      sessionUuid,
      appCode: FRONT_TRACKER_APP_CODE,
      sdkCode: FRONT_TRACKER_APP_CODE,
      integrationMode: 'redirect_web',
      sdkVersion: String(__APP_VERSION__),
      region: __REGION__ || '',
    },
    env: __ENV__,
    debug: __ENV__ !== 'PROD',
  });
}

/**
 *
 * 创建结果页使用的 tracker。
 *
 * 结果页现在不再通过 shared tracker 恢复旧的 redirect session。
 * 它会直接创建自己的 tracker，然后在查单成功后用显式字段补齐当前订单上下文。
 *
 * @returns 结果页使用的 tracker 实例
 */
export function createFrontResultTracker(): CheckoutTracker {
  return createCheckoutTracker({
    session: {
      sessionUuid: createCheckoutUuid(),
      appCode: FRONT_TRACKER_APP_CODE,
      sdkCode: FRONT_TRACKER_APP_CODE,
      integrationMode: 'redirect_web',
      sdkVersion: String(__APP_VERSION__),
      region: __REGION__ || '',
    },
    env: __ENV__,
    debug: __ENV__ !== 'PROD',
  });
}

/**
 *
 * 将订单场景映射为 tracker 允许的场景值。
 *
 * 当前 front 结果页只需要复用业务已有的订阅与独立绑卡标记，
 * 避免在页面组件里重复维护场景判断逻辑。
 *
 * @param payload 结果页返回的订单扩展信息
 * @returns tracker 可接受的场景值
 */
export function resolveTrackerScenario(payload: {
  subscribePaymentFlag?: boolean;
  cashierOrderExtend?: { scenarioTags?: 'zeroPayBinding'[] };
}): CheckoutTrackerScenario {
  if (payload.subscribePaymentFlag) {
    return 'subscription';
  }

  if (payload.cashierOrderExtend?.scenarioTags?.includes('zeroPayBinding')) {
    return 'zero_pay_binding';
  }

  return 'normal';
}

/**
 *
 * 将收银台页的订单数据映射为 tracker order context。
 *
 * front 收银台页拿到的是 `dispatch-order` 成功后的 `IOrderInfo`，
 * 这里仅补齐一期接入文档要求的最小订单字段，不额外引入新的业务抽象。
 *
 * @param orderInfo 收银台壳层接收到的订单信息
 * @returns 可直接写入 tracker 的订单上下文
 */
export function buildFrontCashierOrderContext(
  orderInfo: IOrderInfo
): Partial<CheckoutTrackerOrderContext> {
  return {
    clientId: orderInfo.clientId,
    accId: orderInfo.accId,
    merchantTransactionId: orderInfo.merchantTransactionId,
    orderId: orderInfo.orderId,
    displayLanguage: orderInfo.language,
    scenario: 'normal',
  };
}

/**
 *
 * 将结果页查单结果映射为 tracker order context。
 *
 * front 结果页返回的是 `transactionId`，而 shared tracker 统一使用 `orderId`；
 * 同时 `payRequestId` 来自 URL 显式入参，而不是 shared tracker storage 恢复。
 * 这里把这两类字段统一归一化，保证结果页后续诊断事件拿到当前这笔订单的最小链路信息。
 *
 * @param result 结果页查单返回的支付结果
 * @param payRequestId 结果页 URL 中显式携带的支付请求标识
 * @returns 可直接写入 tracker 的订单上下文
 */
export function buildFrontResultOrderContext(
  result: IPayResult,
  payRequestId?: string
): Partial<CheckoutTrackerOrderContext> {
  return {
    clientId: result.clientId,
    accId: result.accId,
    merchantTransactionId: result.merchantTransactionId,
    orderId: result.transactionId,
    payRequestId,
    displayLanguage: result.language,
    scenario: resolveTrackerScenario(result),
  };
}
